import { useRef } from 'react'

import { appendItem, insertAt, moveItem, removeAt } from './stageLists'

/**
 * Stabile React-Schlüssel für Listen ohne eigene ID.
 *
 * Warum das nötig ist: Campingplätze, Highlights und Abendtipps haben im
 * Datenmodell keine Kennung. Der Array-Index als key sieht beim Tippen
 * harmlos aus, ist aber falsch, sobald ein Eintrag aus der Mitte verschwindet
 * oder die Reihenfolge wechselt: React ordnet dann Feld 3 dem vorherigen
 * Feld 4 zu, behält aber dessen DOM-Knoten samt Fokus und Cursorposition.
 * Der Inhalt springt also eine Zeile hoch, während der Daumen noch im
 * falschen Feld steht. Ein Schlüssel aus dem Inhalt scheidet ebenso aus, weil
 * er sich bei jedem Tastendruck ändern und das Feld neu aufbauen würde.
 *
 * Deshalb führt die Oberfläche eine eigene Schlüsselliste mit, auf die
 * jeder Bedienschritt dieselbe Operation anwendet wie auf die Daten
 * (stageLists.ts). Sie liegt in einem ref, weil sie nur die Zuordnung von
 * Zeile zu DOM-Knoten beschreibt und für sich genommen kein neues Rendern
 * auslösen muss.
 */
export interface RowKeys {
  /** Schlüssel der Zeile an der Stelle index. */
  at: (index: number) => string
  remove: (index: number) => void
  insert: (index: number) => void
  append: () => void
  move: (index: number, direction: -1 | 1) => void
}

export function useRowKeys(count: number): RowKeys {
  const keys = useRef<string[]>([])
  const zaehler = useRef(0)
  const neu = () => `z${(zaehler.current += 1)}`

  // Angleichen an eine Länge, die von aussen kommt: Abgleich mit dem Server
  // oder eine Änderung vom anderen Gerät. Die eigenen Bedienschritte halten
  // die Schlüsselliste bereits nach, dann bleibt hier nichts zu tun.
  while (keys.current.length < count) keys.current = appendItem(keys.current, neu())
  if (keys.current.length > count) keys.current = keys.current.slice(0, count)

  return {
    at: (index) => keys.current[index] ?? `z-${index}`,
    remove: (index) => {
      keys.current = removeAt(keys.current, index)
    },
    insert: (index) => {
      keys.current = insertAt(keys.current, index, neu())
    },
    append: () => {
      keys.current = appendItem(keys.current, neu())
    },
    move: (index, direction) => {
      keys.current = moveItem(keys.current, index, direction)
    },
  }
}
